import React, { useState, useEffect, useRef } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaUserCog,
  FaUserShield,
  FaUserFriends,
  FaUserAlt,
  FaUpload,
  FaEye,
  FaDownload,
  FaExchangeAlt,
  FaTimes,
} from "react-icons/fa";
import axios from '../../Authservices/axiosInstance';
import Modal from "react-modal";
import { motion, AnimatePresence } from "framer-motion";
import "./teamchart.css";

Modal.setAppElement("#root");

const POSITION_HIERARCHY = [
  "CEO",
  "Managing Director",
  "HR",
  "Accounts",
  "Manager",
  "Team Leader",
  "Operators",
];

const MAX_COUNTS = {
  CEO: 30,
  "Managing Director": 30,
  HR: 30,
  Accounts: 30,
  Manager: 30,
  "Team Leader": 30,
  Operators: 30,
};
const POSITION_ICONS = {
  CEO: <FaUserCog />,
  "Managing Director": <FaUserCog />,
  HR: <FaUserCog />,
  Accounts: <FaUserCog />,
  Manager: <FaUserShield />,
  "Team Leader": <FaUserFriends />,
  Operators: <FaUserAlt />,
};
const POSITION_COLORS = {
  CEO: "#8e44ad",
  "Managing Director": "#1dd3b0",
  HR: "#ffb949",
  Accounts: "#3498db",
  Manager: "#2ecc71",
  "Team Leader": "#e67e22",
  Operators: "#e74c3c",
};
const customModalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    maxWidth: "500px",
    width: "90%",
    borderRadius: "15px",
    border: "none",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
    padding: "30px",
    background: "linear-gradient(145deg, #ffffff, #f8f9fa)",
    overflow: "visible",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    backdropFilter: "blur(5px)",
    zIndex: 1000,
  },
};
const OrganizationalHierarchy = () => {
  const [employeesByPosition, setEmployeesByPosition] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const [connections, setConnections] = useState([]);
  const [specialConnections, setSpecialConnections] = useState([]);
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  useEffect(() => {
    fetchEmployees();
  }, []);
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
          "https://backend.ems.uk.hiradentallab.com/api/employees/hierarchy"
      );
      setEmployeesByPosition(response.data);
      const allEmps = Object.values(response.data).flat();
      setAllEmployees(allEmps);
      generateConnections(response.data, allEmps);
      generateSpecialConnections(response.data);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setError(
          "Failed to load organizational hierarchy. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };
  const generateConnections = (employeesByPos, allEmps) => {
    const newConnections = [];
    allEmps.forEach((employee) => {
      if (employee.reportsTo) {
        const supervisor = allEmps.find(
            (e) => e._id === employee.reportsTo._id
        );
        if (supervisor) {
          newConnections.push({
            from: supervisor._id,
            to: employee._id,
            fromPosition: supervisor.position,
            toPosition: employee.position,
            color: POSITION_COLORS[supervisor.position] || "#4a90e2",
          });
        }
      }
    });
    setConnections(newConnections);
  };
  const generateSpecialConnections = (employeesByPos) => {
    const newSpecialConnections = [];
    // Get the first employee from each special position
    const mdEmployee = employeesByPos["Managing Director"]?.[0];
    const hrEmployee = employeesByPos["HR"]?.[0];
    const accountsEmployee = employeesByPos["Accounts"]?.[0];
    // Create connections between them if they exist
    if (mdEmployee && hrEmployee) {
      newSpecialConnections.push({
        from: mdEmployee._id,
        to: hrEmployee._id,
        color: "#3498db",
        type: "horizontal",
      });
    }
    if (mdEmployee && accountsEmployee) {
      newSpecialConnections.push({
        from: mdEmployee._id,
        to: accountsEmployee._id,
        color: "#3498db",
        type: "horizontal",
      });
    }
    setSpecialConnections(newSpecialConnections);
  };
  const getSupervisorsForPosition = (position) => {
    const positionIndex = POSITION_HIERARCHY.indexOf(position);
    if (positionIndex <= 0) return [];
    const supervisorPosition = POSITION_HIERARCHY[positionIndex - 1];
    return allEmployees.filter((emp) => emp.position === supervisorPosition);
  };
  if (loading) {
    return (
        <motion.div
            className="oh-loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
        >
          <div className="spinner"></div>
          Loading organizational hierarchy...
        </motion.div>
    );
  }
  if (error) {
    return (
        <motion.div
            className="oh-error-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
        >
          <div className="oh-error-text">{error}</div>
          <button className="oh-retry-button" onClick={fetchEmployees}>
            Retry
          </button>
        </motion.div>
    );
  }
  return (
      <div className="oh-container" ref={containerRef}>
        <svg
            ref={svgRef}
            className="oh-connections-svg"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              zIndex: 1,
            }}
        >
          <defs>
            <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="#4a90e2" />
            </marker>
            <marker
                id="special-arrowhead"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="#3498db" />
            </marker>
          </defs>
          {connections.map((conn, idx) => {
            const fromEmployee = allEmployees.find((e) => e._id === conn.from);
            const toEmployee = allEmployees.find((e) => e._id === conn.to);
            if (!fromEmployee || !toEmployee) return null;
            return (
                <ConnectionLine
                    key={`conn-${idx}`}
                    fromId={fromEmployee._id}
                    toId={toEmployee._id}
                    containerRef={containerRef}
                    color={conn.color}
                    fromPosition={fromEmployee.position}
                    toPosition={toEmployee.position}
                />
            );
          })}
          {specialConnections.map((conn, idx) => {
            const fromEmployee = allEmployees.find((e) => e._id === conn.from);
            const toEmployee = allEmployees.find((e) => e._id === conn.to);
            if (!fromEmployee || !toEmployee) return null;
            return (
                <SpecialConnectionLine
                    key={`special-conn-${idx}`}
                    fromId={fromEmployee._id}
                    toId={toEmployee._id}
                    containerRef={containerRef}
                    color={conn.color}
                    type={conn.type}
                />
            );
          })}
        </svg>
        <motion.div
            className="oh-header"
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
        >
          <h1 className="oh-header-text">Organizational Hierarchy</h1>
        </motion.div>
        <div
            className="oh-flow-container"
            style={{ position: "relative", zIndex: 2 }}
        >
          {/* CEO Row */}
          {POSITION_HIERARCHY[0] && (
              <PositionRow
                  key={POSITION_HIERARCHY[0]}
                  position={POSITION_HIERARCHY[0]}
                  employees={employeesByPosition[POSITION_HIERARCHY[0]] || []}
                  isFirst={true}
                  isLast={false}
                  onEmployeeAdded={fetchEmployees}
                  onEmployeeDeleted={fetchEmployees}
                  onEmployeeUpdated={fetchEmployees}
                  supervisorOptions={getSupervisorsForPosition(POSITION_HIERARCHY[0])}
                  allEmployees={allEmployees}
                  containerRef={containerRef}
                  positionColor={POSITION_COLORS[POSITION_HIERARCHY[0]]}
              />
          )}
          {/* Special Row for Managing Director, HR, and Accounts */}
          <div className="oh-special-row-container">
            {/* HR Position (Left Side) */}
            {POSITION_HIERARCHY[2] && (
                <div className="oh-special-position-left" data-position="HR">
                  <PositionRow
                      key={POSITION_HIERARCHY[2]}
                      position={POSITION_HIERARCHY[2]}
                      employees={employeesByPosition[POSITION_HIERARCHY[2]] || []}
                      isFirst={false}
                      isLast={false}
                      onEmployeeAdded={fetchEmployees}
                      onEmployeeDeleted={fetchEmployees}
                      onEmployeeUpdated={fetchEmployees}
                      supervisorOptions={getSupervisorsForPosition(
                          POSITION_HIERARCHY[2]
                      )}
                      allEmployees={allEmployees}
                      containerRef={containerRef}
                      positionColor={POSITION_COLORS[POSITION_HIERARCHY[2]]}
                  />
                </div>
            )}
            {/* Managing Director (Center) */}
            {POSITION_HIERARCHY[1] && (
                <div
                    className="oh-special-position-center"
                    data-position="Managing Director"
                >
                  <PositionRow
                      key={POSITION_HIERARCHY[1]}
                      position={POSITION_HIERARCHY[1]}
                      employees={employeesByPosition[POSITION_HIERARCHY[1]] || []}
                      isFirst={false}
                      isLast={false}
                      onEmployeeAdded={fetchEmployees}
                      onEmployeeDeleted={fetchEmployees}
                      onEmployeeUpdated={fetchEmployees}
                      supervisorOptions={getSupervisorsForPosition(
                          POSITION_HIERARCHY[1]
                      )}
                      allEmployees={allEmployees}
                      containerRef={containerRef}
                      positionColor={POSITION_COLORS[POSITION_HIERARCHY[1]]}
                  />
                </div>
            )}
            {/* Accounts Position (Right Side) */}
            {POSITION_HIERARCHY[3] && (
                <div className="oh-special-position-right" data-position="Accounts">
                  <PositionRow
                      key={POSITION_HIERARCHY[3]}
                      position={POSITION_HIERARCHY[3]}
                      employees={employeesByPosition[POSITION_HIERARCHY[3]] || []}
                      isFirst={false}
                      isLast={false}
                      onEmployeeAdded={fetchEmployees}
                      onEmployeeDeleted={fetchEmployees}
                      onEmployeeUpdated={fetchEmployees}
                      supervisorOptions={getSupervisorsForPosition(
                          POSITION_HIERARCHY[3]
                      )}
                      allEmployees={allEmployees}
                      containerRef={containerRef}
                      positionColor={POSITION_COLORS[POSITION_HIERARCHY[3]]}
                  />
                </div>
            )}
          </div>
          {/* Rest of the positions */}
          {POSITION_HIERARCHY.slice(4).map((position, index) => (
              <PositionRow
                  key={position}
                  position={position}
                  employees={employeesByPosition[position] || []}
                  isFirst={false}
                  isLast={index === POSITION_HIERARCHY.slice(4).length - 1}
                  onEmployeeAdded={fetchEmployees}
                  onEmployeeDeleted={fetchEmployees}
                  onEmployeeUpdated={fetchEmployees}
                  supervisorOptions={getSupervisorsForPosition(position)}
                  allEmployees={allEmployees}
                  containerRef={containerRef}
                  positionColor={POSITION_COLORS[position]}
              />
          ))}
        </div>
      </div>
  );
};
const SpecialConnectionLine = ({
                                 fromId,
                                 toId,
                                 containerRef,
                                 color = "#3498db",
                                 type = "horizontal",
                               }) => {
  const [path, setPath] = useState("");
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const updatePath = () => {
      if (!containerRef.current) return;
      const fromElement = document.querySelector(
          `[data-employee-id="${fromId}"]`
      );
      const toElement = document.querySelector(`[data-employee-id="${toId}"]`);
      if (!fromElement || !toElement) {
        setVisible(false);
        return;
      }
      const containerRect = containerRef.current.getBoundingClientRect();
      const fromRect = fromElement.getBoundingClientRect();
      const toRect = toElement.getBoundingClientRect();
      // Calculate positions relative to container
      const fromX = fromRect.left + fromRect.width / 2 - containerRect.left;
      const fromY = fromRect.top + fromRect.height / 2 - containerRect.top;
      const toX = toRect.left + toRect.width / 2 - containerRect.left;
      const toY = toRect.top + toRect.height / 2 - containerRect.top;
      // Create a curved path for horizontal connections
      const midX = (fromX + toX) / 2;
      const curveHeight = 50; // Adjust this for more/less curve
      const pathData = `M${fromX},${fromY} 
      Q${midX},${fromY + curveHeight} ${toX},${toY}`;
      setPath(pathData);
      setVisible(true);
    };
    updatePath();
    const resizeObserver = new ResizeObserver(updatePath);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [fromId, toId, containerRef, type]);
  if (!visible || !path) return null;
  return (
      <path
          d={path}
          stroke={color}
          strokeWidth={2}
          fill="none"
          strokeDasharray="5,5" // Makes the line dashed
          markerEnd="url(#special-arrowhead)"
      />
  );
};
const PositionRow = ({
                       position,
                       employees,
                       isFirst,
                       isLast,
                       onEmployeeAdded,
                       onEmployeeDeleted,
                       onEmployeeUpdated,
                       supervisorOptions,
                       positionColor,
                     }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [fileModalIsOpen, setFileModalIsOpen] = useState(false);
  const [replaceModalIsOpen, setReplaceModalIsOpen] = useState(false);
  const [modalType, setModalType] = useState("add");
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [nameValue, setNameValue] = useState("");
  const [departmentValue, setDepartmentValue] = useState("");
  const [supervisorValue, setSupervisorValue] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [fileModalDimensions, setFileModalDimensions] = useState({
    width: 600,
    height: "auto",
  });
  const imageRef = useRef(null);
  const rowRef = useRef(null);
  // Only show reports to for Team Leaders and Operators
  const shouldShowReportsTo = ["Team Leader", "Operators"].includes(position);
  const openModal = (type, employee = null) => {
    setModalType(type);
    setCurrentEmployee(employee);
    setNameValue(employee?.name || "");
    setDepartmentValue(employee?.department || "");
    setSupervisorValue(employee?.reportsTo?._id || "");
    setModalIsOpen(true);
    setError(null);
  };
  const openFileModal = (employee) => {
    setCurrentEmployee(employee);
    setFileModalIsOpen(true);
    if (employee.fileUrl) {
      setFilePreview(employee.fileUrl);
    } else {
      setFilePreview(null);
    }
  };
  const closeModal = () => {
    if (!isProcessing) {
      setModalIsOpen(false);
      setNameValue("");
      setDepartmentValue("");
      setSupervisorValue("");
      setError(null);
    }
  };
  const closeFileModal = () => {
    if (!fileUploading) {
      setFileModalIsOpen(false);
      setFile(null);
      setFilePreview(null);
    }
  };
  const closeReplaceModal = () => {
    if (!fileUploading) {
      setReplaceModalIsOpen(false);
      setFile(null);
    }
  };
  const handleAdd = async () => {
    if (employees.length >= MAX_COUNTS[position]) {
      alert(`Maximum ${MAX_COUNTS[position]} ${position}(s) allowed.`);
      return;
    }
    openModal("add");
  };
  const handleEdit = (employee) => {
    openModal("edit", employee);
  };
  const handleDelete = (employee) => {
    openModal("delete", employee);
  };
  const handleFileUpload = async () => {
    if (!file) return;
    try {
      setFileUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("employeeId", currentEmployee._id);
      await axios.post("https://backend.ems.uk.hiradentallab.com/api/employees/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      onEmployeeUpdated();
      setReplaceModalIsOpen(false);
      setFileModalIsOpen(true);
    } catch (err) {
      console.error("Error uploading file:", err);
      setError("Failed to upload file. Please try again.");
    } finally {
      setFileUploading(false);
    }
  };
  const handleFileReplace = () => {
    setReplaceModalIsOpen(true);
    setFileModalIsOpen(false);
  };
  const handleFileDownload = async () => {
    if (!currentEmployee?.fileUrl) return;
    try {
      const response = await axios.get(
          `https://backend.ems.hiradentallab.com${currentEmployee.fileUrl}`,
          {
            responseType: "blob",
          }
      );
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const filename =
          currentEmployee.fileUrl.split("/").pop() || "employee_file";
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Error downloading file:", err);
      setError("Failed to download file. Please try again.");
    }
  };
  const handleConfirm = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      if (modalType === "add" || modalType === "edit") {
        if (!nameValue.trim()) {
          setError("Name cannot be empty");
          return;
        }
        if (
            ["Manager", "Team Leader"].includes(position) &&
            !departmentValue.trim()
        ) {
          setError("Department cannot be empty for this position");
          return;
        }
        if (shouldShowReportsTo && !supervisorValue && modalType === "add") {
          setError("Please select a supervisor");
          return;
        }
      }
      if (modalType === "add") {
        await axios.post("https://backend.ems.uk.hiradentallab.com/api/employees", {
          name: nameValue.trim(),
          position: position,
          department: departmentValue.trim(),
          reportsTo: shouldShowReportsTo ? supervisorValue : null,
        });
        onEmployeeAdded();
      } else if (modalType === "edit") {
        await axios.put(
            `https://backend.ems.uk.hiradentallab.com/api/employees/${currentEmployee._id}`,
            {
              name: nameValue.trim(),
              position: currentEmployee.position,
              department: departmentValue.trim(),
              reportsTo: shouldShowReportsTo ? supervisorValue : null,
            }
        );
        onEmployeeUpdated();
      } else if (modalType === "delete") {
        await axios.delete(
            `https://backend.ems.uk.hiradentallab.com/api/employees/${currentEmployee._id}`
        );
        onEmployeeDeleted();
      }
      closeModal();
    } catch (err) {
      console.error(
          `Error ${
              modalType === "delete"
                  ? "deleting"
                  : modalType === "edit"
                      ? "updating"
                      : "adding"
          } employee:`,
          err
      );
      setError(
          `Failed to ${modalType} employee. ${
              err.response?.data?.message || "Please try again."
          }`
      );
    } finally {
      setIsProcessing(false);
    }
  };
  const handleImageLoad = () => {
    if (imageRef.current && currentEmployee?.fileType !== "pdf") {
      const img = imageRef.current;
      const maxWidth = Math.min(
          img.naturalWidth + 100,
          window.innerWidth - 100
      );
      const maxHeight = Math.min(
          img.naturalHeight + 100,
          window.innerHeight - 100
      );
      setFileModalDimensions({
        width: maxWidth,
        height: maxHeight,
      });
    } else {
      setFileModalDimensions({ width: 800, height: 600 });
    }
  };
  const getFileModalStyles = () => {
    return {
      content: {
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        transform: "translate(-50%, -50%)",
        width: `${fileModalDimensions.width}px`,
        height:
            fileModalDimensions.height === "auto"
                ? "auto"
                : `${fileModalDimensions.height}px`,
        maxWidth: "90vw",
        maxHeight: "90vh",
        borderRadius: "15px",
        border: "none",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
        padding: "20px",
        background: "linear-gradient(145deg, #ffffff, #f8f9fa)",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
      },
      overlay: {
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(5px)",
        zIndex: 1000,
      },
    };
  };
  const employeeRows = [];
  for (let i = 0; i < employees.length; i += 5) {
    employeeRows.push(employees.slice(i, i + 5));
  }
  return (
      <motion.div
          className={`oh-role-section ${isFirst ? "oh-first-row" : ""}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          ref={rowRef}
      >
        <div
            className="oh-heading"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{ borderBottom: `3px solid ${positionColor}` }}
        >
          <span className="oh-role-icon">{POSITION_ICONS[position]}</span>
          {position}
          {isHovered && employees.length < MAX_COUNTS[position] && (
              <motion.button
                  className="oh-add-button"
                  onClick={handleAdd}
                  title={`Add ${position}`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
              >
                <FaPlus />
              </motion.button>
          )}
        </div>
        <div className="oh-boxes-container">
          {employeeRows.map((row, rowIndex) => (
              <div key={`row-${rowIndex}`} className="oh-boxes-row">
                {row.map((employee) => (
                    <motion.div
                        key={employee._id}
                        className="oh-box"
                        style={{ borderTop: `3px solid ${positionColor}` }}
                        whileHover={{ y: -5 }}
                        transition={{ type: "spring", stiffness: 300 }}
                        data-employee-id={employee._id}
                    >
                      <div>
                        <div className="oh-employee-name">{employee.name}</div>
                        {employee.department && (
                            <div className="oh-department-badge">
                              {employee.department}
                            </div>
                        )}
                        {shouldShowReportsTo && employee.reportsTo && (
                            <div className="oh-reports-to">
                              Reports to: {employee.reportsTo.name}
                            </div>
                        )}
                        <div className="oh-button-group">
                          <motion.button
                              className="oh-icon-button"
                              onClick={() => handleEdit(employee)}
                              title="Edit"
                              whileHover={{ scale: 1.2, color: "#4CAF50" }}
                              whileTap={{ scale: 0.9 }}
                          >
                            <FaEdit />
                          </motion.button>
                          <motion.button
                              className="oh-icon-button"
                              onClick={() => handleDelete(employee)}
                              title="Delete"
                              whileHover={{ scale: 1.2, color: "#f44336" }}
                              whileTap={{ scale: 0.9 }}
                          >
                            <FaTrash />
                          </motion.button>
                          <motion.button
                              className="oh-icon-button"
                              onClick={() => openFileModal(employee)}
                              title={employee.fileUrl ? "View File" : "Upload File"}
                              whileHover={{
                                scale: 1.2,
                                color: employee.fileUrl ? "#2196F3" : "#FF9800",
                              }}
                              whileTap={{ scale: 0.9 }}
                          >
                            {employee.fileUrl ? <FaEye /> : <FaUpload />}
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                ))}
              </div>
          ))}
        </div>
        {!isLast && (
            <motion.div
                className="oh-vertical-connector"
                animate={{ height: [0, 40], opacity: [0, 1] }}
                transition={{ duration: 0.5, delay: 0.2 }}
            />
        )}
        <Modal
            isOpen={modalIsOpen}
            onRequestClose={closeModal}
            style={customModalStyles}
            contentLabel={`${modalType} Employee Modal`}
        >
          <AnimatePresence>
            {modalIsOpen && (
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                >
                  <h2 className="oh-modal-title">
                    {modalType === "add"
                        ? `Add New ${position}`
                        : modalType === "edit"
                            ? "Edit"
                            : "Confirm Deletion"}
                  </h2>
                  {error && <div className="oh-error-text">{error}</div>}
                  {modalType !== "delete" ? (
                      <>
                        <div className="oh-form-group">
                          <label className="oh-modal-label">Name</label>
                          <input
                              type="text"
                              value={nameValue}
                              onChange={(e) => setNameValue(e.target.value)}
                              className="oh-modal-input"
                              placeholder={`Enter ${position} name`}
                              autoFocus
                              disabled={isProcessing}
                          />
                        </div>
                        {(position === "Manager" ||
                            position === "Team Leader" ||
                            position === "Operators") && (
                            <div className="oh-form-group">
                              <label className="oh-modal-label">Department</label>
                              <input
                                  type="text"
                                  value={departmentValue}
                                  onChange={(e) => setDepartmentValue(e.target.value)}
                                  className="oh-modal-input"
                                  placeholder="Enter department"
                                  disabled={isProcessing}
                                  required={
                                      position === "Manager" || position === "Team Leader"
                                  }
                              />
                            </div>
                        )}
                        {shouldShowReportsTo && (
                            <div className="oh-form-group">
                              <label className="oh-modal-label">
                                Reports to{" "}
                                {
                                  POSITION_HIERARCHY[
                                  POSITION_HIERARCHY.indexOf(position) - 1
                                      ]
                                }
                              </label>
                              <select
                                  value={supervisorValue}
                                  onChange={(e) => setSupervisorValue(e.target.value)}
                                  className="oh-modal-input"
                                  disabled={
                                      isProcessing || supervisorOptions.length === 0
                                  }
                                  required={modalType === "add"}
                              >
                                <option value="">
                                  Select{" "}
                                  {
                                    POSITION_HIERARCHY[
                                    POSITION_HIERARCHY.indexOf(position) - 1
                                        ]
                                  }
                                </option>
                                {supervisorOptions.map((supervisor) => (
                                    <option key={supervisor._id} value={supervisor._id}>
                                      {supervisor.name} ({supervisor.position})
                                    </option>
                                ))}
                              </select>
                            </div>
                        )}
                        <div className="oh-modal-button-group">
                          <motion.button
                              className="oh-modal-cancel-button"
                              onClick={closeModal}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              disabled={isProcessing}
                          >
                            Cancel
                          </motion.button>
                          <motion.button
                              className="oh-modal-confirm-button"
                              onClick={handleConfirm}
                              disabled={
                                  !nameValue.trim() ||
                                  isProcessing ||
                                  ((position === "Manager" ||
                                          position === "Team Leader") &&
                                      !departmentValue.trim()) ||
                                  (shouldShowReportsTo &&
                                      modalType === "add" &&
                                      !supervisorValue)
                              }
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                          >
                            {isProcessing
                                ? "Processing..."
                                : modalType === "add"
                                    ? "Add"
                                    : "Update"}
                          </motion.button>
                        </div>
                      </>
                  ) : (
                      <>
                        <p className="oh-modal-text">
                          Are you sure you want to delete{" "}
                          <strong>{currentEmployee?.name}</strong>?
                        </p>
                        <div className="oh-modal-button-group">
                          <motion.button
                              className="oh-modal-cancel-button"
                              onClick={closeModal}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              disabled={isProcessing}
                          >
                            Cancel
                          </motion.button>
                          <motion.button
                              className="oh-modal-delete-button"
                              onClick={handleConfirm}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              disabled={isProcessing}
                          >
                            {isProcessing ? "Deleting..." : "Delete"}
                          </motion.button>
                        </div>
                      </>
                  )}
                </motion.div>
            )}
          </AnimatePresence>
        </Modal>
        <Modal
            isOpen={fileModalIsOpen}
            onRequestClose={closeFileModal}
            style={getFileModalStyles()}
            contentLabel="File View Modal"
        >
          <AnimatePresence>
            {fileModalIsOpen && (
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    style={{ display: "flex", flexDirection: "column", flex: 1 }}
                >
                  <div className="oh-modal-header">
                    <h2 className="oh-modal-title">
                      {currentEmployee?.name}'s File
                    </h2>
                    <button
                        className="oh-modal-close-button"
                        onClick={closeFileModal}
                        disabled={fileUploading}
                    >
                      <FaTimes />
                    </button>
                  </div>
                  {error && <div className="oh-error-text">{error}</div>}
                  {currentEmployee?.fileUrl ? (
                      <div
                          style={{ display: "flex", flexDirection: "column", flex: 1 }}
                      >
                        <div
                            className="oh-file-preview-container"
                            style={{ flex: 1 }}
                        >
                          {currentEmployee.fileType === "pdf" ? (
                              <iframe
                                  src={`http://localhost:5544${currentEmployee.fileUrl}`}
                                  title="File Preview"
                                  className="oh-file-preview"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    border: "none",
                                    minHeight: "400px",
                                  }}
                              />
                          ) : (
                              <img
                                  ref={imageRef}
                                  src={`https://backend.ems.hiradentallab.com${currentEmployee.fileUrl}`}
                                  alt="File Preview"
                                  className="oh-file-preview"
                                  onLoad={handleImageLoad}
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: "calc(100vh - 200px)",
                                    objectFit: "contain",
                                  }}
                              />
                          )}
                        </div>
                        <div
                            className="oh-modal-button-group"
                            style={{ marginTop: "20px" }}
                        >
                          <motion.button
                              className="oh-modal-download-button"
                              onClick={handleFileDownload}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              disabled={fileUploading}
                          >
                            <FaDownload /> Download
                          </motion.button>
                          <motion.button
                              className="oh-modal-replace-button"
                              onClick={handleFileReplace}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              disabled={fileUploading}
                          >
                            <FaExchangeAlt /> Replace
                          </motion.button>
                        </div>
                      </div>
                  ) : (
                      <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px",
                          }}
                      >
                        <div className="oh-form-group">
                          <label className="oh-modal-label">Select File</label>
                          <input
                              type="file"
                              onChange={(e) => setFile(e.target.files[0])}
                              className="oh-modal-input"
                              disabled={fileUploading}
                          />
                        </div>
                        <div className="oh-modal-button-group">
                          <motion.button
                              className="oh-modal-cancel-button"
                              onClick={closeFileModal}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              disabled={fileUploading}
                          >
                            Cancel
                          </motion.button>
                          <motion.button
                              className="oh-modal-confirm-button"
                              onClick={handleFileUpload}
                              disabled={!file || fileUploading}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                          >
                            {fileUploading ? "Uploading..." : "Upload"}
                          </motion.button>
                        </div>
                      </div>
                  )}
                </motion.div>
            )}
          </AnimatePresence>
        </Modal>
        <Modal
            isOpen={replaceModalIsOpen}
            onRequestClose={closeReplaceModal}
            style={customModalStyles}
            contentLabel="File Replace Modal"
        >
          <AnimatePresence>
            {replaceModalIsOpen && (
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                >
                  <h2 className="oh-modal-title">
                    Replace File for {currentEmployee?.name}
                  </h2>
                  {error && <div className="oh-error-text">{error}</div>}
                  <div className="oh-form-group">
                    <label className="oh-modal-label">Select New File</label>
                    <input
                        type="file"
                        onChange={(e) => setFile(e.target.files[0])}
                        className="oh-modal-input"
                        disabled={fileUploading}
                    />
                  </div>
                  <div className="oh-modal-button-group">
                    <motion.button
                        className="oh-modal-cancel-button"
                        onClick={closeReplaceModal}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        disabled={fileUploading}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                        className="oh-modal-confirm-button"
                        onClick={handleFileUpload}
                        disabled={!file || fileUploading}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                      {fileUploading ? "Uploading..." : "Upload"}
                    </motion.button>
                  </div>
                </motion.div>
            )}
          </AnimatePresence>
        </Modal>
      </motion.div>
  );
};
const ConnectionLine = ({
                          fromId,
                          toId,
                          containerRef,
                          color = "#4a90e2",
                          fromPosition,
                          toPosition,
                        }) => {
  const [path, setPath] = useState("");
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const updatePath = () => {
      if (!containerRef.current) return;
      const fromElement = document.querySelector(
          `[data-employee-id="${fromId}"]`
      );
      const toElement = document.querySelector(`[data-employee-id="${toId}"]`);
      if (!fromElement || !toElement) {
        setVisible(false);
        return;
      }
      const containerRect = containerRef.current.getBoundingClientRect();
      const fromRect = fromElement.getBoundingClientRect();
      const toRect = toElement.getBoundingClientRect();
      // Calculate positions relative to container
      const fromX = fromRect.left + fromRect.width / 2 - containerRect.left;
      const fromY = fromRect.bottom - containerRect.top;
      const toX = toRect.left + toRect.width / 2 - containerRect.left;
      const toY = toRect.top - containerRect.top;
      // Calculate control points for smooth curve
      const controlY = fromY + (toY - fromY) * 0.3;
      const controlX1 = fromX;
      const controlX2 = toX;
      // Create Bezier curve path
      const pathData = `M${fromX},${fromY} 
                       C${controlX1},${controlY} ${controlX2},${controlY} ${toX},${toY}`;
      setPath(pathData);
      setVisible(true);
    };
    updatePath();
    const resizeObserver = new ResizeObserver(updatePath);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [fromId, toId, containerRef]);
  if (!visible || !path) return null;
  return (
      <path
          d={path}
          stroke={color}
          strokeWidth={2}
          fill="none"
          markerEnd="url(#arrowhead)"
      />
  );
};
export default OrganizationalHierarchy;
